/**
 * Fit-to-width layout and the per-user column preference.
 *
 * The archive NEVER scrolls sideways. Given the container width, `fitColumns`
 * keeps the user's visible columns in their chosen order and drops the least
 * important (highest `priority`) ones until the rest fit at their minimum
 * widths; spare width is then shared by `grow`. What was dropped is reported
 * so the table can say "+N" and show those values in the row expander.
 *
 * The preference itself (`ColumnPrefs`: ordered ids + visibility + density)
 * is saved per logged-in user via the saved-layouts API (screen id
 * `archive.columns`), with localStorage as the offline cache. `reconcilePrefs`
 * makes any stored preference total against the live registry: unknown ids
 * are dropped, new columns (a stat added to the catalog, a new custom stat)
 * are slotted in at their default position with their default visibility.
 */
import {
  defaultPrefs as defaultPrefsGeneric,
  fitColumns as fitColumnsGeneric,
  reconcilePrefs as reconcilePrefsGeneric,
  type ColumnPref,
  type ColumnPrefs,
  type Density,
  type FitOptions as FitOptionsGeneric,
  type FitResult as FitResultGeneric,
  type FittedColumn as FittedColumnGeneric,
} from '../../components/table-layout';

import type { ArchiveColumn } from './archive-columns';

// The preference model and fit algorithm are shared with every list page
// (components/table-layout.ts); Archive keeps its own names and column type.
export type { ColumnPref, ColumnPrefs, Density } from '../../components/table-layout';
export { moveColumn, moveColumnTo, toggleColumn } from '../../components/table-layout';

/** Wire shape stored under `PUT /users/v1/layouts/user/archive.columns`. */
export interface ArchiveColumnsLayout {
  kind: 'archive-columns';
  version: 1;
  columns: ColumnPref[];
  density?: Density;
}

export const ARCHIVE_COLUMNS_SCREEN_ID = 'archive.columns';
export const ARCHIVE_COLUMNS_LAYOUT_NAME = 'Archive columns';
export const ARCHIVE_COLUMNS_STORAGE_KEY = 'gridiron.archive-columns.v2';

/** Width reserved for the row-actions column (kebab). */
export const ACTIONS_COLUMN_PX = 40;

export function defaultPrefs(columns: readonly ArchiveColumn[]): ColumnPrefs {
  return defaultPrefsGeneric(columns);
}

/** Stored preference → a total preference over the live registry. */
export function reconcilePrefs(
  stored: Partial<ColumnPrefs> | null | undefined,
  columns: readonly ArchiveColumn[]
): ColumnPrefs {
  return reconcilePrefsGeneric(stored, columns);
}

export function toLayout(prefs: ColumnPrefs): ArchiveColumnsLayout {
  return {
    kind: 'archive-columns',
    version: 1,
    columns: prefs.columns.map((entry) => ({ id: entry.id, visible: entry.visible })),
    density: prefs.density,
  };
}

/** A saved layout record's JSON → prefs (null when it is not ours). */
export function fromLayout(layout: unknown): Partial<ColumnPrefs> | null {
  if (typeof layout !== 'object' || layout === null) return null;
  const record = layout as Partial<ArchiveColumnsLayout>;
  if (record.kind !== 'archive-columns' || !Array.isArray(record.columns)) return null;
  return {
    columns: record.columns.filter(
      (entry): entry is ColumnPref =>
        typeof entry === 'object' && entry !== null && typeof entry.id === 'string'
    ),
    density: record.density === 'comfortable' ? 'comfortable' : 'compact',
  };
}

export function readCachedPrefs(
  storage: Pick<Storage, 'getItem'> | undefined = globalThis.localStorage
): Partial<ColumnPrefs> | null {
  try {
    const raw = storage?.getItem(ARCHIVE_COLUMNS_STORAGE_KEY);
    return raw === null || raw === undefined || raw === '' ? null : fromLayout(JSON.parse(raw));
  } catch {
    return null;
  }
}

export function writeCachedPrefs(
  prefs: ColumnPrefs,
  storage: Pick<Storage, 'setItem'> | undefined = globalThis.localStorage
): void {
  try {
    storage?.setItem(ARCHIVE_COLUMNS_STORAGE_KEY, JSON.stringify(toLayout(prefs)));
  } catch {
    // Private mode with storage disabled must not break the table.
  }
}

/* -------------------------------------------------------------------- fit */

export type FittedColumn = FittedColumnGeneric<ArchiveColumn>;
export type FitResult = FitResultGeneric<ArchiveColumn>;
export type FitOptions = FitOptionsGeneric<ArchiveColumn>;

/**
 * Keep the user's visible columns in order; while the minimum widths don't
 * fit, drop the visible column with the worst (highest) priority. Pinned
 * columns are never dropped. Spare width goes to columns by `grow`.
 */
export function fitColumns(
  columns: readonly ArchiveColumn[],
  prefs: ColumnPrefs,
  options: FitOptions
): FitResult {
  return fitColumnsGeneric(columns, prefs, options);
}

/* --------------------------------------------------------------- measure */

export interface MeasureOptions {
  /** CSS font shorthand for cells (e.g. `400 13px Inter`). */
  cellFont: string;
  /** CSS font shorthand for header labels. */
  headerFont: string;
  /** Horizontal cell padding (both sides) plus chrome, px. */
  cellPadding: number;
  /** Extra px for header chrome (sort arrow + filter dot). */
  headerChrome: number;
  /** Which quantile of non-blank row widths to fit (1 = the longest; `maxWidth` still caps it). */
  quantile?: number;
}

type TextMeasurer = (text: string, font: string) => number;

let sharedContext: CanvasRenderingContext2D | null | undefined;

/** Canvas text measurer, or null where there is no canvas (jsdom). */
export function canvasMeasurer(): TextMeasurer | null {
  if (sharedContext === undefined) {
    try {
      sharedContext =
        typeof document === 'undefined' ? null : document.createElement('canvas').getContext('2d');
    } catch {
      sharedContext = null;
    }
  }
  const context = sharedContext;
  if (context === null || context === undefined) return null;
  return (text, font) => {
    context.font = font;
    return context.measureText(text).width;
  };
}

/**
 * The width each column needs to show its content without an ellipsis for
 * `quantile` of the plays (and its whole header label). Values are CSS px at
 * the given fonts, padding included — fed to `fitColumns({ measured })`.
 */
export function measureColumnWidths<P>(
  columns: readonly ArchiveColumn[],
  plays: readonly P[],
  cellText: (column: ArchiveColumn, play: P) => string,
  measure: TextMeasurer,
  options: MeasureOptions
): Map<string, number> {
  const quantile = options.quantile ?? 1;
  const out = new Map<string, number>();
  for (const column of columns) {
    // Sparse columns (tags, ST stats on a mostly-scrimmage game) are sized to
    // the plays that HAVE a value, not drowned by the blanks.
    const widths = plays
      .map((play) => cellText(column, play))
      .filter((text) => text !== '' && text !== '—')
      .map((text) => measure(text, options.cellFont));
    widths.sort((a, b) => a - b);
    const index = Math.min(widths.length - 1, Math.floor(quantile * widths.length));
    const content = widths.length === 0 ? 0 : (widths[index] ?? 0);
    const header =
      Math.max(
        measure(column.header, options.headerFont),
        column.kicker === undefined ? 0 : measure(column.kicker, options.headerFont) * 0.8
      ) + options.headerChrome;
    out.set(column.id, Math.max(content + options.cellPadding, header));
  }
  return out;
}
